/* bb-size-memory.js — Phase 1, Step 1 of the personalisation engine.
   Captures and persists the customer's size so other surfaces can use it.

   Public API (nothing else should touch localStorage directly):
     BBSize.getProfile()            -> whole profile object
     BBSize.get(productType)        -> size entry for that product's category
     BBSize.set(category, payload, confidence)
     BBSize.key(category)           -> index lookup key, e.g. "L" or "L|Regular"
     BBSize.classify(productType)   -> 'top' | 'bottom' | null
     BBSize.onChange(fn)            -> subscribe; also fires `bb:size-changed` on document

   Storage layers (see brief):
     localStorage `bb_size_profile`  — source of truth on the storefront
     cart attribute `preferred_size` — flushed on cart mutation, survives to the order
     customer metafield              — written SERVER-SIDE ONLY by scripts/sync-size-metafields.js.
                                       The storefront cannot write customer metafields; do not try.

   CATALOGUE NOTES (verified 16 Aug 2026 against all 682 products):
   - 643 products expose a single option named `Size`. Colour is a separate product,
     so there is no colour dimension to resolve on active products.
   - Only 6 ACTIVE products use two options (`Size` + `Length`) — the new Classic Pant
     range. There is no option literally named "Waist"; the waist measurement is encoded
     in the Size value, e.g. `L . 34-35"`. So `bottom.waist` below holds the size token
     ("L"), with the raw option value kept alongside it.
   - Products with `Color`+`Size` or `Pocket`/`Sleeves` options are all DRAFT bundles.
*/
(function () {
  'use strict';

  var KEY = 'bb_size_profile';
  var VERSION = 1;
  var HISTORY_CAP = 50;
  var CONFIDENCE_RANK = { inferred: 1, explicit: 2, purchased: 3 };

  /* ---------------------------------------------------------------- storage */
  // Safari private mode throws on any localStorage access, so everything is wrapped.
  function readProfile() {
    try {
      var raw = localStorage.getItem(KEY);
      if (!raw) return blank();
      var p = JSON.parse(raw);
      if (!p || p.version !== VERSION) return blank();
      if (!Array.isArray(p.history)) p.history = [];
      return p;
    } catch (e) {
      return blank();
    }
  }

  function writeProfile(p) {
    try {
      localStorage.setItem(KEY, JSON.stringify(p));
    } catch (e) {
      /* quota or private mode — the in-memory copy still serves this page */
    }
    cache = p;
  }

  function blank() {
    return { version: VERSION, top: null, bottom: null, history: [] };
  }

  var cache = readProfile();

  /* ------------------------------------------------------------- classifier */
  // Product type is not a clean top/bottom signal on its own — "Winter" holds
  // hoodies, "Air Denim" is jeans, "Formal Flex" is a trouser. Keyword match on
  // type first, then handle/title, and return null rather than guess. A wrong
  // guess cross-contaminates top and bottom sizes, which is worse than no answer.
  var BOTTOM_WORDS = ['pant', 'trouser', 'jogger', 'short', 'sweatpant', 'jean',
                      'denim', 'cargo', 'pajama', 'pyjama', 'chino', 'flex'];
  var TOP_WORDS = ['shirt', 'tee', 'tshirt', 't-shirt', 'polo', 'hoodie', 'sweatshirt',
                   'overshirt', 'jacket', 'kurta', 'henley', 'knit', 'winter', 'capsule'];

  function classify(productType, extra) {
    var hay = String(productType || '').toLowerCase();
    if (extra) hay += ' ' + String(extra).toLowerCase();
    if (!hay.trim()) return null;
    // bottoms first: "sweatpants" must not be caught by the "shirt" in "sweatshirt"
    for (var i = 0; i < BOTTOM_WORDS.length; i++) {
      if (hay.indexOf(BOTTOM_WORDS[i]) !== -1) return 'bottom';
    }
    for (var j = 0; j < TOP_WORDS.length; j++) {
      if (hay.indexOf(TOP_WORDS[j]) !== -1) return 'top';
    }
    return null;
  }

  /* ------------------------------------------------------------ size tokens */
  var CANONICAL = ['XS', 'S', 'M', 'L', 'XL', 'XXL', '3XL', '4XL'];

  // `L . 34-35"` -> `L`;  `XS .27-28"` -> `XS`;  `L` -> `L`
  function tokenise(raw) {
    if (!raw) return null;
    var t = String(raw).split('.')[0].split('/')[0].trim().toUpperCase();
    return CANONICAL.indexOf(t) !== -1 ? t : null;
  }

  /* --------------------------------------------------------------- profile */
  function nowISO() { return new Date().toISOString(); }

  function outranks(next, current) {
    if (!current) return true;
    return (CONFIDENCE_RANK[next] || 0) >= (CONFIDENCE_RANK[current] || 0);
  }

  function pushHistory(p, entry) {
    p.history.unshift(entry);
    if (p.history.length > HISTORY_CAP) p.history.length = HISTORY_CAP;
  }

  /**
   * @param {'top'|'bottom'} category
   * @param {{size?:string, raw?:string, length?:string, hasLengthOption?:boolean,
   *          productType?:string, action?:string}} payload
   * @param {'inferred'|'explicit'|'purchased'} confidence
   *
   * `hasLengthOption: false` means "this product has no Length dimension", which
   * CLEARS any remembered length. Without it a length picked on a Classic Pant
   * leaks onto every later bottom and produces keys like "XXL|Short" for products
   * that have no lengths at all.
   */
  function set(category, payload, confidence) {
    if (category !== 'top' && category !== 'bottom') return null;
    payload = payload || {};
    confidence = confidence || 'inferred';

    var token = payload.size ? tokenise(payload.size) || String(payload.size).toUpperCase() : null;
    var current = cache[category];

    // A length-only change on an existing bottom entry must not be blocked by rank.
    var lengthOnly = !token && payload.length && category === 'bottom' && current;
    if (!token && !lengthOnly) return null;
    if (!lengthOnly && !outranks(confidence, current && current.confidence)) return cache[category];

    var entry;
    if (category === 'top') {
      entry = { size: token, raw: payload.raw || payload.size || token, confidence: confidence, updated: nowISO() };
    } else {
      var nextLength;
      if (payload.hasLengthOption === false) {
        nextLength = null;                       // product has no Length dimension
      } else if (payload.length != null) {
        nextLength = payload.length;             // explicit pick
      } else {
        nextLength = (current && current.length) || null;
      }
      entry = {
        waist: token || (current && current.waist) || null,
        raw: payload.raw || payload.size || (current && current.raw) || null,
        length: nextLength,
        confidence: lengthOnly ? (current && current.confidence) || confidence : confidence,
        updated: nowISO()
      };
    }

    cache[category] = entry;
    pushHistory(cache, {
      size: token || (entry.waist || null),
      length: category === 'bottom' ? entry.length : undefined,
      category: category,
      product_type: payload.productType || null,
      action: payload.action || 'selected',
      ts: nowISO()
    });
    writeProfile(cache);
    announce(category, entry);
    queueCartAttribute();
    return entry;
  }

  function get(productTypeOrCategory) {
    var c = (productTypeOrCategory === 'top' || productTypeOrCategory === 'bottom')
      ? productTypeOrCategory
      : classify(productTypeOrCategory);
    return c ? cache[c] : null;
  }

  // Lookup key for bb-size-index.json. Bottoms with a Length option key as
  // "L|Regular"; everything else is the bare token.
  function key(category) {
    var e = cache[category];
    if (!e) return null;
    if (category === 'top') return e.size || null;
    if (!e.waist) return null;
    return e.length ? e.waist + '|' + e.length : e.waist;
  }

  /**
   * Ordered lookup keys, most specific first. Consumers should try each in turn
   * and concatenate results.
   *
   * Only 6 active products have a Length option, so "L|Regular" matches ~6 items
   * while plain "L" matches ~64. Without this fallback a customer who ever picked
   * a length would see a near-empty rail forever, because every length-free bottom
   * lives under the bare token.
   *
   * @returns {string[]} e.g. ["L|Regular", "L"], or ["L"], or []
   */
  function keys(category) {
    var e = cache[category];
    if (!e) return [];
    if (category === 'top') return e.size ? [e.size] : [];
    if (!e.waist) return [];
    return e.length ? [e.waist + '|' + e.length, e.waist] : [e.waist];
  }

  /* -------------------------------------------------------------- listeners */
  var subscribers = [];
  function onChange(fn) { if (typeof fn === 'function') subscribers.push(fn); }
  function announce(category, entry) {
    subscribers.forEach(function (fn) {
      try { fn(category, entry, cache); } catch (e) {}
    });
    try {
      document.dispatchEvent(new CustomEvent('bb:size-changed', {
        detail: { category: category, entry: entry, profile: cache }
      }));
    } catch (e) {}
  }

  /* ------------------------------------------------------- cart attribute */
  // Writing on every tap would be chatty and would create carts for people who
  // never add anything. Instead mark dirty and flush on the theme's own
  // post-cart-mutation signal, or immediately if a cart already has items.
  var dirty = false;

  function queueCartAttribute() {
    dirty = true;
    try {
      fetch('/cart.js', { headers: { 'Accept': 'application/json' } })
        .then(function (r) { return r.json(); })
        .then(function (c) { if (c && c.item_count > 0) flushCartAttribute(); })
        .catch(function () {});
    } catch (e) {}
  }

  function flushCartAttribute() {
    if (!dirty) return;
    var value = JSON.stringify({ top: key('top'), bottom: key('bottom') });
    dirty = false;
    try {
      fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ attributes: { preferred_size: value } })
      }).catch(function () { dirty = true; });
    } catch (e) { dirty = true; }
  }

  document.addEventListener('bb:section-reloaded', flushCartAttribute);

  /* ------------------------------------------------- product type discovery */
  // Shopify renders ShopifyAnalytics.meta from content_for_header on every page.
  function pdpProductType() {
    try {
      var m = window.ShopifyAnalytics && window.ShopifyAnalytics.meta;
      if (m && m.product && m.product.type) return m.product.type;
    } catch (e) {}
    return null;
  }

  // For cards (collection, search, recently viewed) the type is not in the DOM.
  // Fetch it once per handle and keep it for the session — this only fires on a
  // deliberate size tap, so it is a rare, small request.
  var metaCache = {};
  function metaForHandle(handle, cb) {
    if (!handle) return cb(null);
    if (metaCache[handle] !== undefined) return cb(metaCache[handle]);
    try {
      var stored = sessionStorage.getItem('bb_meta_' + handle);
      if (stored) { metaCache[handle] = JSON.parse(stored); return cb(metaCache[handle]); }
    } catch (e) {}
    fetch('/products/' + handle + '.js', { headers: { 'Accept': 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(function (p) {
        var names = (p && p.options || []).map(function (o) {
          return String(o && o.name ? o.name : o).toLowerCase();
        });
        var m = {
          type: (p && p.type) || null,
          hasLength: names.indexOf('length') !== -1 || names.indexOf('inseam') !== -1
        };
        metaCache[handle] = m;
        try { sessionStorage.setItem('bb_meta_' + handle, JSON.stringify(m)); } catch (e) {}
        cb(m);
      })
      .catch(function () { metaCache[handle] = null; cb(null); });
  }

  /* ------------------------------------------------------------- capture */
  // PDP buy box. Delegated so it survives bb:section-reloaded re-renders.
  document.addEventListener('change', function (e) {
    var radio = e.target.closest && e.target.closest('[data-bb-size-radio]');
    if (radio) {
      var pill = radio.closest('.bb-size-pill');
      var raw = (pill && pill.getAttribute('data-size')) || radio.value;
      var type = pdpProductType();
      var cat = classify(type, document.title);
      // The buy box publishes the resolved Length option index; -1 means this
      // product has no Length dimension, so any remembered length must be cleared.
      var bb = radio.closest('.beyours-bb');
      var lenIdx = bb ? parseInt(bb.getAttribute('data-bb-length-idx') || '-1', 10) : -1;
      if (cat) {
        set(cat, {
          size: raw, raw: raw, productType: type, action: 'selected',
          hasLengthOption: lenIdx >= 0
        }, 'explicit');
      }
      return;
    }

    var lengthPill = e.target.closest && e.target.closest('.bb-length-pill');
    if (lengthPill) {
      var lv = lengthPill.getAttribute('data-length');
      var t2 = pdpProductType();
      // A Length option only exists on bottoms in this catalogue.
      if (lv) set('bottom', { length: lv, productType: t2, action: 'selected' }, 'explicit');
    }
  });

  // Quick-add cards (snippets/quick-add-card.liquid) — collection, search,
  // featured, recently viewed.
  document.addEventListener('click', function (e) {
    var btn = e.target.closest && e.target.closest('[data-qa-size]');
    if (!btn) return;
    var root = btn.closest('[data-qa]');
    var handle = root && root.getAttribute('data-product-handle');
    var raw = btn.textContent.trim();
    metaForHandle(handle, function (m) {
      if (!m) return;
      var cat = classify(m.type);
      if (cat) {
        set(cat, {
          size: raw, raw: raw, productType: m.type, action: 'selected',
          hasLengthOption: m.hasLength
        }, 'explicit');
      }
    });
  });

  /* ----------------------------------------------------------------- expose */
  window.BBSize = {
    getProfile: function () { return JSON.parse(JSON.stringify(cache)); },
    get: get,
    set: set,
    key: key,
    keys: keys,
    classify: classify,
    tokenise: tokenise,
    onChange: onChange,
    _flush: flushCartAttribute
  };
})();
